import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTACT_MEMBERS_COLOUMS, LIMIT_PER_PAGE } from "../../Common/Constants";
import { Loader } from "../../Common/Loader";
import { Pagination } from "../../Common/Pagination";
import { Table } from "../../Common/Table";
import { debounce } from "../../Common/debounce";
import useDebounce from "../../Common/useDebounce";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../Redux/Slice/Common/PaginationSlice";
import { getContactGroupMembersList } from "../../Redux/Slice/Contacts/Contact Group/ContactGroupMembersListSlice";
import { deleteContactGroupMember } from "../../Redux/Slice/Contacts/Contact Group/DeleteGroupMemberSlice";
import moment from "moment/moment";

export const ContactGroupUsersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = location.pathname.split("/")[2];
  const [columns, setColumns] = useState(CONTACT_MEMBERS_COLOUMS);
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);
  const [errorModel, setErrorModel] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successModel, setSuccessModel] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmModel, setCOnfirmModel] = useState(false);
  const [selectedRow, setselectedRow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);
  const contactGroupMembers = useSelector(
    (state) => state.contactGroupMembers.groupMembersListData
  );


  const membersList = useMemo(() => {
    return Array.isArray(contactGroupMembers?.data?.data)
      ? contactGroupMembers?.data?.data?.map((member) => {
          const formattedMember = { ...member };
          return formattedMember;
        })
      : [];
  }, [contactGroupMembers]);

  const getGroupMembersList = async (page = 1, limit) => {
    setIsLoading(true);
    const result = await dispatch(
      getContactGroupMembersList({ searchText, page, groupId, limit })
    );
    const newColoums = Array.isArray(result?.payload?.data?.columns)
      ? result?.payload?.data?.columns?.map((col) => ({
          label: col?.label,
          key: col?.key,
          className: "cellText",
        }))
      : [];

    setColumns([...CONTACT_MEMBERS_COLOUMS, ...newColoums]);
    setIsLoading(false);
    dispatch(setPage(result?.payload?.pagination?.pagination?.currentPage));
    dispatch(
      setTotalItems(result?.payload?.pagination?.pagination?.totalItems)
    );
    dispatch(
      setTotalPages(result?.payload?.pagination?.pagination?.totalPages)
    );
  };

  const debouncedGetUserList = useCallback(debounce(getGroupMembersList, 500), [
    searchQuery,
  ]);

  useEffect(() => {
    debouncedGetUserList(1, localItemsPerPage);
  }, [searchQuery, debouncedGetUserList, groupId]);

  const onEdit = (rowData) => {
    navigate("/edit-contact/" + rowData?.id);
  };

  const onDeleteClick = (row) => {
    setCOnfirmModel(true);
    setselectedRow(row);
  };

  const onDelete = async () => {
    setCOnfirmModel(false);
    const result = await dispatch(
      deleteContactGroupMember({ groupId: groupId, contactId: selectedRow?.id })
    );
    if (
      deleteContactGroupMember.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      setSuccessMessage("Contact Deleted Successfully!");
      setSuccessModel(true);
      setTimeout(() => {
        setSuccessModel(false);
        getGroupMembersList(1, localItemsPerPage);
      }, 1500);
    } else {
      setErrorMessage(result?.payload?.error?.message);
      setErrorModel(true);
      setTimeout(() => {
        setErrorModel(false);
      }, 1500);
    }
  };

  return (
    <div>
      {errorModel ? <ErrorModel message={errorMessage} /> : ""}
      {successModel ? <SuccessModel message={successMessage} /> : ""}
      {isLoading ? <Loader /> : ""}
      {confirmModel ? (
        <ConfirmModel
          onAccept={onDelete}
          onReject={() => {
            setCOnfirmModel(false);
          }}
          title="Delete Contact!"
          message="Are you sure you want to Delete the Contact?"
        />
      ) : (
        ""
      )}
      <div className="page__body">
        <div className="tableWrap">
          <div className="tableWrap__head">
            <div className="formRow m_0 formRow--flexEnd">
              <div className="formCol formCol--flex">
                <div className="formCol flexField">
                  <input
                    className="form__input form__input--search"
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value.trimStart());
                    }}
                    placeholder="Search Name, Email"
                  />
                </div>
              </div>
            </div>
            <div className="btn__group">
              <button
                className="btn btn__primary"
                onClick={() =>
                  navigate("/add-contact", {
                    state: {
                      contactGroupId: groupId,
                    },
                  })
                }
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.00008 1.1665V12.8332M1.16675 6.99984H12.8334"
                    stroke="white"
                    stroke-width="1.67"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Add Contact
              </button>
            </div>
          </div>
          <Table
            columns={columns}
            data={membersList}
            onEdit={onEdit}
            onDelete={onDeleteClick}
            hasAction="true"
            noDataMsg={
              searchQuery && searchQuery?.length
                ? `No Contact Found!`
                : `No Contact!`
            }
          />
        </div>
      </div>
      <Pagination 
        apiMethod={getGroupMembersList} 
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </div>
  );
};
